import { z } from 'zod';
import type { DocumentDraft, IDocumentDraftRepository, SaveDocumentDraftInput } from '../../../interfaces/src';
import type { TypedSupabaseClient } from '../client';

const uuid = z.string().uuid();
const revision = z.number().int().positive();
const documentType = z.enum(['sales.quote', 'sales.order', 'sales.delivery', 'sales.invoice', 'sales.credit_note', 'purchases.order', 'purchases.reception', 'purchases.invoice', 'purchases.credit_note']);
const saveSchema = z.object({
  id: uuid, scopeKey: z.string().min(1).max(500), documentType,
  documentId: uuid.nullable(), originStoreId: uuid.nullable(),
  payload: z.record(z.string(), z.unknown()), clientRequestId: uuid,
  expectedRevision: revision.nullable(),
});
const rowSchema = z.object({
  id: uuid, scope_key: z.string(), document_type: documentType,
  document_id: uuid.nullable(), origin_store_id: uuid.nullable(),
  payload: z.record(z.string(), z.unknown()), revision,
  status: z.enum(['draft', 'validating', 'validated', 'deleted']), client_request_id: uuid,
  validation_request_id: uuid.nullable(), validated_document_id: uuid.nullable(),
  created_at: z.string(), updated_at: z.string(),
});
function mapDraft(raw: unknown): DocumentDraft {
  const row = rowSchema.parse(raw);
  return { id: row.id, scopeKey: row.scope_key, documentType: row.document_type,
    documentId: row.document_id, originStoreId: row.origin_store_id,
    payload: row.payload, revision: row.revision, status: row.status,
    clientRequestId: row.client_request_id, validationRequestId: row.validation_request_id,
    validatedDocumentId: row.validated_document_id, createdAt: row.created_at, updatedAt: row.updated_at };
}

/** Every operation is authenticated and scoped server-side; callers never supply tenant/owner IDs. */
export class SupabaseDocumentDraftRepository implements IDocumentDraftRepository {
  constructor(private readonly supabase: TypedSupabaseClient) {}

  private async access(operation: string, input: Record<string, unknown>): Promise<unknown> {
    // Additive RPC is not in the production-generated schema until this migration is applied.
    // Keep the temporary schema boundary inside the adapter; runtime responses are validated below.
    const { data, error } = await this.supabase.rpc('document_draft_access' as never, {
      p_operation: operation, p_input: input,
    } as never);
    if (error) throw error;
    return data;
  }
  async get(id: string): Promise<DocumentDraft | null> {
    const data = await this.access('get', { id: uuid.parse(id) });
    return data === null ? null : mapDraft(data);
  }
  async findByScope(scopeKey: string): Promise<DocumentDraft | null> {
    const data = await this.access('find', { scopeKey: z.string().min(1).max(500).parse(scopeKey) });
    return data === null ? null : mapDraft(data);
  }
  async list(type: string, originStoreId?: string | null): Promise<DocumentDraft[]> {
    const data = await this.access('list', { documentType: documentType.parse(type),
      ...(originStoreId === undefined ? {} : { originStoreId: uuid.nullable().parse(originStoreId) }) });
    return z.array(z.unknown()).parse(data).map(mapDraft);
  }
  async save(input: SaveDocumentDraftInput): Promise<DocumentDraft> {
    return mapDraft(await this.access('save', saveSchema.parse(input)));
  }
  async deleteDraft(id: string, expectedRevision: number): Promise<DocumentDraft> {
    return mapDraft(await this.access('delete', { id: uuid.parse(id), expectedRevision: revision.parse(expectedRevision) }));
  }
  async beginValidation(id: string, expectedRevision: number): Promise<DocumentDraft> {
    return mapDraft(await this.access('begin', { id: uuid.parse(id), expectedRevision: revision.parse(expectedRevision) }));
  }
  async completeValidation(id: string, expectedRevision: number, documentId: string): Promise<DocumentDraft> {
    return mapDraft(await this.access('complete', { id: uuid.parse(id), expectedRevision: revision.parse(expectedRevision), documentId: uuid.parse(documentId) }));
  }
  async cancelValidation(id: string, expectedRevision: number): Promise<DocumentDraft> {
    return mapDraft(await this.access('cancel', { id: uuid.parse(id), expectedRevision: revision.parse(expectedRevision) }));
  }
}
